/**
 * Structural validation of a JSON Schema document (draft 2020-12).
 *
 * A prompt's `outputSchema` is handed to a provider as a structured-output
 * instruction, so a document that is merely valid JSON is not enough: `{"type":
 * "objct"}` parses cleanly and then fails at the provider, mid-execution, after the
 * tokens are spent. This checks the parts the spec constrains and a caller can get
 * wrong — the keyword vocabulary, the value types each keyword takes, and that
 * subschemas are themselves schemas.
 *
 * Deliberately NOT a full validator. `ajv` is present only transitively here, and a
 * complete implementation would have to resolve `$ref` across documents, honour
 * `$dynamicAnchor`, and track `$schema` dialects — none of which a prompt's output
 * shape needs. What it does catch is every mistake a person makes typing one by
 * hand: a misspelled type, a keyword given the wrong shape, a nested schema that is
 * not an object.
 *
 * Shared by the API's Zod schema and the editor's live check, so the two can never
 * disagree about what is acceptable.
 */

/** The seven `type` values the spec defines. Anything else is a typo. */
const TYPES = new Set(['null', 'boolean', 'object', 'array', 'number', 'string', 'integer'])

/** Keywords whose value must itself be a schema. */
const SCHEMA_VALUED = ['items', 'contains', 'not', 'if', 'then', 'else', 'additionalProperties', 'propertyNames']

/** Keywords whose value is an object whose every VALUE is a schema. */
const SCHEMA_MAP_VALUED = ['properties', 'patternProperties', '$defs', 'definitions']

/** Keywords whose value is an array of schemas. */
const SCHEMA_ARRAY_VALUED = ['allOf', 'anyOf', 'oneOf', 'prefixItems']

/** Keywords taking a non-negative integer. */
const NON_NEGATIVE_INTEGER = [
	'minLength',
	'maxLength',
	'minItems',
	'maxItems',
	'minProperties',
	'maxProperties',
	'minContains',
	'maxContains',
]

/** Keywords taking any number. */
const NUMERIC = ['minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf']

export interface JsonSchemaProblem {
	/** Dotted path to the offending keyword, `(root)` at the top. */
	path: string
	message: string
}

/**
 * Every structural problem in the document, or an empty array when it is sound.
 *
 * Returns ALL problems rather than the first: someone typing a schema by hand
 * usually has several, and fixing them one round-trip at a time is the slowest way
 * to arrive at a valid document.
 */
export function validateJsonSchema(document: unknown): JsonSchemaProblem[] {
	const problems: JsonSchemaProblem[] = []
	walk(document, '(root)', problems)
	return problems
}

/** Convenience for a caller that only needs the verdict. */
export const isValidJsonSchema = (document: unknown): boolean => validateJsonSchema(document).length === 0

function walk(node: unknown, path: string, problems: JsonSchemaProblem[]): void {
	// `true` and `false` ARE schemas in draft 2020-12 — they accept and reject
	// everything respectively, and appear as `additionalProperties: false` constantly.
	if (typeof node === 'boolean') return

	if (typeof node !== 'object' || node === null || Array.isArray(node)) {
		problems.push({ path, message: 'A schema must be an object, or the boolean true/false' })
		return
	}

	const schema = node as Record<string, unknown>

	checkType(schema, path, problems)
	checkRequired(schema, path, problems)
	checkEnumAndConst(schema, path, problems)
	checkNumbers(schema, path, problems)
	checkNested(schema, path, problems)
}

function checkType(schema: Record<string, unknown>, path: string, problems: JsonSchemaProblem[]): void {
	const type = schema.type
	if (type === undefined) return

	const named = Array.isArray(type) ? type : [type]
	for (const entry of named) {
		if (typeof entry !== 'string' || !TYPES.has(entry)) {
			problems.push({
				path: `${path}.type`,
				message: `"${String(entry)}" is not a JSON Schema type — expected one of ${[...TYPES].join(', ')}`,
			})
		}
	}
}

function checkRequired(schema: Record<string, unknown>, path: string, problems: JsonSchemaProblem[]): void {
	if (schema.required === undefined) return

	if (!Array.isArray(schema.required) || schema.required.some((name) => typeof name !== 'string')) {
		problems.push({ path: `${path}.required`, message: '`required` must be an array of property names' })
		return
	}

	// A required property the schema never declares is the mistake this catches most
	// often: the provider is told to produce a field the shape does not describe.
	const properties = schema.properties
	if (typeof properties === 'object' && properties !== null && !Array.isArray(properties)) {
		const declared = new Set(Object.keys(properties as Record<string, unknown>))
		for (const name of schema.required as string[]) {
			if (!declared.has(name)) {
				problems.push({
					path: `${path}.required`,
					message: `"${name}" is required but not declared in \`properties\``,
				})
			}
		}
	}
}

function checkEnumAndConst(schema: Record<string, unknown>, path: string, problems: JsonSchemaProblem[]): void {
	if (schema.enum !== undefined && (!Array.isArray(schema.enum) || schema.enum.length === 0)) {
		problems.push({ path: `${path}.enum`, message: '`enum` must be a non-empty array' })
	}
}

function checkNumbers(schema: Record<string, unknown>, path: string, problems: JsonSchemaProblem[]): void {
	for (const keyword of NON_NEGATIVE_INTEGER) {
		const value = schema[keyword]
		if (value === undefined) continue
		if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
			problems.push({ path: `${path}.${keyword}`, message: `\`${keyword}\` must be a non-negative integer` })
		}
	}

	for (const keyword of NUMERIC) {
		const value = schema[keyword]
		if (value === undefined) continue
		if (typeof value !== 'number' || Number.isNaN(value)) {
			problems.push({ path: `${path}.${keyword}`, message: `\`${keyword}\` must be a number` })
		}
	}
}

function checkNested(schema: Record<string, unknown>, path: string, problems: JsonSchemaProblem[]): void {
	for (const keyword of SCHEMA_VALUED) {
		if (schema[keyword] !== undefined) walk(schema[keyword], `${path}.${keyword}`, problems)
	}

	for (const keyword of SCHEMA_MAP_VALUED) {
		const value = schema[keyword]
		if (value === undefined) continue

		if (typeof value !== 'object' || value === null || Array.isArray(value)) {
			problems.push({ path: `${path}.${keyword}`, message: `\`${keyword}\` must be an object of schemas` })
			continue
		}

		for (const [name, sub] of Object.entries(value as Record<string, unknown>)) {
			walk(sub, `${path}.${keyword}.${name}`, problems)
		}
	}

	for (const keyword of SCHEMA_ARRAY_VALUED) {
		const value = schema[keyword]
		if (value === undefined) continue

		if (!Array.isArray(value) || value.length === 0) {
			problems.push({ path: `${path}.${keyword}`, message: `\`${keyword}\` must be a non-empty array of schemas` })
			continue
		}

		value.forEach((sub, index) => walk(sub, `${path}.${keyword}[${index}]`, problems))
	}
}
